<template>
  <div class="space-y-4">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h4 class="text-sm font-medium text-gray-900 dark:text-white">Fallback Chain</h4>
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Configure fallback models to try when primary routing targets fail. Drag to reorder.
        </p>
      </div>
      <Button variant="outline" size="sm" @click="addFallback" :disabled="!hasAvailableModels">
        <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 4v16m8-8H4"
          />
        </svg>
        Add Fallback
      </Button>
    </div>

    <!-- Fallback Chain List -->
    <div v-if="localFallbacks.length > 0" class="space-y-3">
      <div
        v-for="(fallback, index) in localFallbacks"
        :key="index"
        :draggable="true"
        @dragstart="handleDragStart(index, $event)"
        @dragover="handleDragOver(index, $event)"
        @dragend="handleDragEnd"
        @drop="handleDrop(index, $event)"
        :class="[
          'bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-all cursor-move',
          draggedIndex === index ? 'opacity-50' : '',
          dragOverIndex === index ? 'border-blue-500 dark:border-blue-400 border-2' : '',
        ]"
      >
        <div class="flex items-start space-x-3">
          <!-- Drag Handle & Order Badge -->
          <div class="flex-shrink-0 flex flex-col items-center space-y-1">
            <div
              class="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 flex items-center justify-center text-sm font-medium"
            >
              {{ index + 1 }}
            </div>
            <svg
              class="w-5 h-5 text-gray-400 dark:text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 8h16M4 16h16"
              />
            </svg>
          </div>

          <!-- Fallback Configuration -->
          <div class="flex-1 space-y-3">
            <!-- Backend Type Selector -->
            <div>
              <label
                :for="`fallback-type-${index}`"
                class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Backend Type
              </label>
              <Select
                :id="`fallback-type-${index}`"
                v-model="fallback.backend_type"
                :options="backendTypeOptions"
                @update:model-value="handleBackendTypeChange(index)"
                class="w-full"
              />
            </div>

            <!-- Model Selector -->
            <div>
              <label
                :for="`fallback-model-${index}`"
                class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Model
              </label>
              <Select
                :id="`fallback-model-${index}`"
                v-model="fallback.backend_id"
                :options="getModelOptions(fallback.backend_type)"
                placeholder="Select a model"
                class="w-full"
              />
              <p
                v-if="getModelOptions(fallback.backend_type).length === 0"
                class="mt-1 text-xs text-amber-600 dark:text-amber-400"
              >
                No {{ fallback.backend_type === 'base' ? 'base' : 'custom' }} models available.
                Please enable models first.
              </p>
            </div>

            <!-- Model Info (if selected) -->
            <div
              v-if="getSelectedModelInfo(fallback)"
              class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-2"
            >
              <div class="flex items-center justify-between text-xs">
                <span class="text-gray-500 dark:text-gray-400">Cost:</span>
                <span class="font-medium text-gray-900 dark:text-white">
                  ${{ getSelectedModelInfo(fallback)?.input_cost_1m?.toFixed(2) || '0.00' }} / ${{
                    getSelectedModelInfo(fallback)?.output_cost_1m?.toFixed(2) || '0.00'
                  }}
                  <span class="text-gray-500 dark:text-gray-400">/1M tokens</span>
                </span>
              </div>
            </div>

            <!-- Fallback Order Info -->
            <div class="bg-purple-50 dark:bg-purple-900/20 rounded p-2">
              <p class="text-xs text-purple-700 dark:text-purple-400">
                <span class="font-medium">Fallback Priority {{ index + 1 }}:</span>
                {{ getFallbackDescription(index) }}
              </p>
            </div>
          </div>

          <!-- Remove Button -->
          <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- icon-only remove-row action, not a standalone Button -->
          <Button
            variant="ghost"
            size="sm"
            @click="removeFallback(index)"
            class="flex-shrink-0 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </Button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- dashed empty-state box, not a Card (no dashed/empty variant exists yet) -->
    <div
      v-else
      class="bg-gray-50 dark:bg-gray-800/50 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center"
    >
      <svg
        class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No fallback models</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Add fallback models to handle failures in primary routing targets.
      </p>
      <div class="mt-6">
        <Button variant="primary" size="sm" @click="addFallback" :disabled="!hasAvailableModels">
          <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add First Fallback
        </Button>
      </div>
      <p v-if="!hasAvailableModels" class="mt-3 text-xs text-amber-600 dark:text-amber-400">
        No models available. Please enable base models or create custom models first.
      </p>
    </div>

    <!-- Info Box -->
    <div
      v-if="localFallbacks.length > 0"
      class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3"
    >
      <div class="flex items-start space-x-2">
        <svg
          class="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div class="flex-1">
          <p class="text-sm font-medium text-blue-900 dark:text-blue-300">
            Fallback Chain: {{ localFallbacks.length }}
            {{ localFallbacks.length === 1 ? 'model' : 'models' }}
          </p>
          <p class="mt-1 text-xs text-blue-700 dark:text-blue-400">
            When all primary routing targets fail, the system will try each fallback model in order
            until one succeeds. Drag models to reorder the fallback sequence.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { Button, Select } from '@asabdev/asab-ui';
  import type { FallbackTarget, BaseModelResponse, CustomModelResponse } from '../types';
  import type { SelectOption } from '@asabdev/asab-ui';

  interface Props {
    modelValue: FallbackTarget[];
    availableBaseModels: BaseModelResponse[];
    availableCustomModels: CustomModelResponse[];
  }

  interface Emits {
    (e: 'update:modelValue', value: FallbackTarget[]): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();

  // Local state - create a mutable copy of the modelValue
  const localFallbacks = ref<FallbackTarget[]>([...props.modelValue]);

  // Drag and drop state
  const draggedIndex = ref<number | null>(null);
  const dragOverIndex = ref<number | null>(null);

  // Backend type options
  const backendTypeOptions: SelectOption[] = [
    { value: 'base', label: 'Base Model' },
    { value: 'custom', label: 'Custom Model (BYOM)' },
  ];

  // Computed properties
  const hasAvailableModels = computed(() => {
    return props.availableBaseModels.length > 0 || props.availableCustomModels.length > 0;
  });

  defineExpose({ hasAvailableModels });

  // Get model options based on backend type
  const getModelOptions = (backendType: 'base' | 'custom'): SelectOption[] => {
    if (backendType === 'base') {
      return props.availableBaseModels.map(model => ({
        value: model.name,
        label: `${model.name} (${model.provider})`,
      }));
    } else {
      return props.availableCustomModels.map(model => ({
        value: model.name,
        label: model.name,
      }));
    }
  };

  // Get selected model info for display
  const getSelectedModelInfo = (fallback: FallbackTarget) => {
    if (!fallback.backend_id) return null;

    if (fallback.backend_type === 'base') {
      return props.availableBaseModels.find(m => m.name === fallback.backend_id);
    } else {
      return props.availableCustomModels.find(m => m.name === fallback.backend_id);
    }
  };

  // Get fallback description
  const getFallbackDescription = (index: number): string => {
    if (index === 0) {
      return 'Tried first if all primary routing targets fail';
    } else if (index === localFallbacks.value.length - 1) {
      return 'Last resort fallback option';
    } else {
      return `Tried if previous ${index} fallback${index > 1 ? 's' : ''} fail`;
    }
  };

  // Methods
  const addFallback = () => {
    const newFallback: FallbackTarget = {
      backend_id: '',
      backend_type: 'base',
    };
    localFallbacks.value.push(newFallback);
    emitUpdate();
  };

  const removeFallback = (index: number) => {
    localFallbacks.value.splice(index, 1);
    emitUpdate();
  };

  const handleBackendTypeChange = (index: number) => {
    // Reset backend_id when backend_type changes
    localFallbacks.value[index].backend_id = '';
    emitUpdate();
  };

  // Drag and drop handlers
  const handleDragStart = (index: number, event: DragEvent) => {
    draggedIndex.value = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', String(index));
    }
  };

  const handleDragOver = (index: number, event: DragEvent) => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    dragOverIndex.value = index;
  };

  const handleDragEnd = () => {
    draggedIndex.value = null;
    dragOverIndex.value = null;
  };

  const handleDrop = (dropIndex: number, event: DragEvent) => {
    event.preventDefault();
    const dragIndex = draggedIndex.value;

    if (dragIndex !== null && dragIndex !== dropIndex) {
      // Reorder the array
      const draggedItem = localFallbacks.value[dragIndex];
      const newFallbacks = [...localFallbacks.value];

      // Remove from old position
      newFallbacks.splice(dragIndex, 1);

      // Insert at new position
      newFallbacks.splice(dropIndex, 0, draggedItem);

      localFallbacks.value = newFallbacks;
      emitUpdate();
    }

    handleDragEnd();
  };

  const emitUpdate = () => {
    emit('update:modelValue', [...localFallbacks.value]);
  };

  // Watch for changes in localFallbacks and emit updates
  watch(
    localFallbacks,
    () => {
      emitUpdate();
    },
    { deep: true }
  );

  // Watch for external changes to modelValue (e.g., form reset)
  watch(
    () => props.modelValue,
    newValue => {
      // Only update if the reference changed (not deep equality)
      if (newValue !== localFallbacks.value) {
        localFallbacks.value = [...newValue];
      }
    }
  );
</script>
